import {
    getCorporateRequests,
    getCorporateRequestById,
    updateCorporateRequestStatus,
    getPendingRequestsCount,
} from '@/api/corporateRequests.js';
import constants from '@/constants';

export default {
    state: {
        // List state
        requests: [],
        isRequestsLoading: false,

        // Pagination - NEW STRUCTURE
        pagination: {
            total: 0,
            limit: constants.PAGINATION.DEFAULT_LIMIT,
            offset: constants.PAGINATION.DEFAULT_OFFSET,
            count: 0,
        },
        links: {
            self: null,
            first: null,
            prev: null,
            next: null,
            last: null,
        },

        // Filters
        searchQuery: '',
        statusFilter: constants.CORPORATE_REQUEST_STATUS.ALL,
        sortField: 'created_at:desc',

        // Detail view state
        isShowRequestDetail: false,
        requestDetailData: null,
        isDetailLoading: false,

        // Badge count
        pendingCount: 0,
        isPendingCountLoading: false,
    },

    getters: {
        // List getters
        CORPORATE_REQUESTS: (state) => state.requests,
        IS_REQUESTS_LOADING: (state) => state.isRequestsLoading,

        // Pagination getters
        CORPORATE_REQUESTS_PAGINATION: (state) => state.pagination,
        CORPORATE_REQUESTS_LINKS: (state) => state.links,

        // Computed pagination values for UI
        CORPORATE_REQUESTS_CURRENT_PAGE: (state) => {
            const { offset, limit } = state.pagination;
            return Math.floor(offset / limit);
        },
        CORPORATE_REQUESTS_TOTAL_PAGES: (state) => {
            const { total, limit } = state.pagination;
            return Math.max(0, Math.ceil(total / limit) - 1);
        },
        CORPORATE_REQUESTS_DISPLAY_RANGE: (state) => {
            const { offset, limit, total } = state.pagination;
            if (total === 0) return '0-0 of 0';
            const start = offset + 1;
            const end = Math.min(offset + limit, total);
            return `${start}-${end} of ${total}`;
        },

        // Filter getters
        CORPORATE_REQUESTS_SEARCH: (state) => state.searchQuery,
        CORPORATE_REQUESTS_STATUS_FILTER: (state) => state.statusFilter,
        CORPORATE_REQUESTS_SORT: (state) => state.sortField,

        // Detail getters
        IS_SHOW_REQUEST_DETAIL: (state) => state.isShowRequestDetail,
        REQUEST_DETAIL_DATA: (state) => state.requestDetailData,
        IS_DETAIL_LOADING: (state) => state.isDetailLoading,

        // Badge getter
        PENDING_REQUESTS_COUNT: (state) => state.pendingCount,
        IS_PENDING_COUNT_LOADING: (state) => state.isPendingCountLoading,
    },

    mutations: {
        // List mutations
        SET_CORPORATE_REQUESTS(state, payload) {
            state.requests = payload;
        },
        SET_REQUESTS_LOADING(state, payload) {
            state.isRequestsLoading = payload;
        },

        // Pagination mutations
        SET_PAGINATION(state, payload) {
            state.pagination = payload;
        },
        SET_LINKS(state, payload) {
            state.links = payload;
        },
        SET_LIMIT(state, payload) {
            state.pagination.limit = payload;
            state.pagination.offset = 0;
        },
        SET_OFFSET(state, payload) {
            state.pagination.offset = payload;
        },

        // Filter mutations
        SET_SEARCH_QUERY(state, payload) {
            state.searchQuery = payload;
        },
        SET_STATUS_FILTER(state, payload) {
            state.statusFilter = payload;
        },
        SET_SORT_FIELD(state, payload) {
            state.sortField = payload;
            state.pagination.offset = 0;
        },

        // Detail mutations
        IS_SHOW_REQUEST_DETAIL(state, payload) {
            state.isShowRequestDetail = payload;
        },
        REQUEST_DETAIL_SET(state, payload) {
            state.requestDetailData = payload;
        },
        SET_DETAIL_LOADING(state, payload) {
            state.isDetailLoading = payload;
        },

        // Badge mutations
        SET_PENDING_COUNT(state, payload) {
            state.pendingCount = payload;
        },
        SET_PENDING_COUNT_LOADING(state, payload) {
            state.isPendingCountLoading = payload;
        },

        // Update single request in list
        UPDATE_REQUEST_IN_LIST(state, updatedRequest) {
            const index = state.requests.findIndex(
                (r) => r.id === updatedRequest.id
            );
            if (index !== -1) {
                state.requests.splice(index, 1, updatedRequest);
            }
        },
    },

    actions: {
        async loadCorporateRequests({ commit, state }) {
            commit('SET_REQUESTS_LOADING', true);
            try {
                const params = {
                    limit: state.pagination.limit,
                    offset: state.pagination.offset,
                    sort: state.sortField,
                };

                // Add optional filters
                const statusFilter = state.statusFilter;
                if (
                    statusFilter &&
                    statusFilter !== constants.CORPORATE_REQUEST_STATUS.ALL
                ) {
                    params.status_filter = statusFilter;
                }

                const trimmedSearch = state.searchQuery?.trim();
                if (trimmedSearch) {
                    params.search = trimmedSearch;
                }

                const response = await getCorporateRequests(params);
                commit('SET_CORPORATE_REQUESTS', response.data || []);
                commit(
                    'SET_PAGINATION',
                    response.pagination || {
                        total: 0,
                        limit: constants.PAGINATION.DEFAULT_LIMIT,
                        offset: 0,
                        count: 0,
                    }
                );
                commit('SET_LINKS', response.links || {});
            } catch (error) {
                console.error(
                    '[corporateRequests] Error loading requests:',
                    error
                );
                commit('SET_CORPORATE_REQUESTS', []);
                commit('SET_PAGINATION', {
                    total: 0,
                    limit: constants.PAGINATION.DEFAULT_LIMIT,
                    offset: 0,
                    count: 0,
                });
            } finally {
                commit('SET_REQUESTS_LOADING', false);
            }
        },

        async changePage({ commit, dispatch }, pageAction) {
            const state = this.state.corporateRequests;
            const limit = state.pagination.limit;
            let newOffset = state.pagination.offset;

            switch (pageAction) {
                case 'first':
                    newOffset = 0;
                    break;
                case 'prev':
                    newOffset = Math.max(0, state.pagination.offset - limit);
                    break;
                case 'next':
                    newOffset = Math.min(
                        state.pagination.total - limit,
                        state.pagination.offset + limit
                    );
                    break;
                case 'last':
                    newOffset =
                        Math.floor((state.pagination.total - 1) / limit) *
                        limit;
                    break;
            }

            commit('SET_OFFSET', newOffset);
            await dispatch('loadCorporateRequests');
        },

        async applyFilters({ commit, dispatch }) {
            // Reset to first page when filters change
            commit('SET_OFFSET', 0);
            await dispatch('loadCorporateRequests');
        },

        async loadRequestDetail({ commit }, requestId) {
            commit('SET_DETAIL_LOADING', true);
            try {
                const request = await getCorporateRequestById(requestId);
                commit('REQUEST_DETAIL_SET', request);
                commit('IS_SHOW_REQUEST_DETAIL', true);
            } catch (error) {
                console.error(
                    '[corporateRequests] Error loading request detail:',
                    error
                );
                commit('REQUEST_DETAIL_SET', null);
                commit('IS_SHOW_REQUEST_DETAIL', false);
            } finally {
                commit('SET_DETAIL_LOADING', false);
            }
        },

        async updateRequestStatus(
            { commit, dispatch },
            { requestId, status, admin_notes }
        ) {
            try {
                const updatedRequest = await updateCorporateRequestStatus(
                    requestId,
                    { status, admin_notes }
                );

                // Update request in list
                commit('UPDATE_REQUEST_IN_LIST', updatedRequest);

                // Update detail view if open
                const currentDetailId = this.getters.REQUEST_DETAIL_DATA?.id;
                if (currentDetailId === requestId) {
                    commit('REQUEST_DETAIL_SET', updatedRequest);
                }

                // Refresh pending count if status changed to/from pending
                await dispatch('loadPendingCount');

                return updatedRequest;
            } catch (error) {
                console.error(
                    '[corporateRequests] Error updating request status:',
                    error
                );
                throw error;
            }
        },

        async loadPendingCount({ commit }) {
            commit('SET_PENDING_COUNT_LOADING', true);
            try {
                const count = await getPendingRequestsCount();
                commit('SET_PENDING_COUNT', count);
            } catch (error) {
                console.error(
                    '[corporateRequests] Error loading pending count:',
                    error
                );
                commit('SET_PENDING_COUNT', 0);
            } finally {
                commit('SET_PENDING_COUNT_LOADING', false);
            }
        },
    },
};
